/*-----------------------------------------------------------------------------/
/ EXC product title fit
/
/ Keeps the product title on two lines. Long titles step down in size until
/ they fit, short ones step up, and the step never goes further than the
/ allowed pixels from the size set in the Title block, so the title never ends
/ up tiny or huge. If a title still needs a third line at the smallest allowed
/ size it gets one, which is better than clipping it.
/
/ <title-fit data-max-lines="2" data-max-shrink="3" data-max-grow="3">
/ Notes: hq/projects/excdesigns/theme-build-notes.md
/-----------------------------------------------------------------------------*/
class TitleFit extends HTMLElement {
  connectedCallback() {
    this.heading = this.querySelector('.product-single__title');
    if (!this.heading) return;

    this.maxLines = parseInt(this.dataset.maxLines, 10) || 2;
    this.maxShrink = parseFloat(this.dataset.maxShrink) || 3;
    this.maxGrow = parseFloat(this.dataset.maxGrow) || 3;
    this.step = 0.5;
    this.lastWidth = null;

    this.fit = this.fit.bind(this);

    // Web fonts change the measurement, so fit again once they are ready
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.fit);
    }
    this.fit();

    this.observer = new ResizeObserver(() => {
      // Same width means this is the echo of our own font-size write, not a real resize
      if (this.getBoundingClientRect().width === this.lastWidth) return;
      this.fit();
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  #lineCount() {
    const styles = getComputedStyle(this.heading);
    const lineHeight = parseFloat(styles.lineHeight);
    if (!lineHeight) return 1;
    return Math.round(this.heading.getBoundingClientRect().height / lineHeight);
  }

  fit() {
    // Start from the size the Title block set, so this stays reversible and
    // re-fits correctly when the window changes size
    this.heading.style.fontSize = '';
    const base = parseFloat(getComputedStyle(this.heading).fontSize);
    let size = base;

    if (this.#lineCount() > this.maxLines) {
      const min = base - this.maxShrink;
      while (size > min && this.#lineCount() > this.maxLines) {
        size = Math.max(min, size - this.step);
        this.heading.style.fontSize = `${size}px`;
      }
    } else if (this.#lineCount() < this.maxLines) {
      const max = base + this.maxGrow;
      while (size < max) {
        const next = Math.min(max, size + this.step);
        this.heading.style.fontSize = `${next}px`;
        if (this.#lineCount() > this.maxLines) {
          this.heading.style.fontSize = `${size}px`;
          break;
        }
        size = next;
      }
    }

    this.dataset.lines = this.#lineCount();
    // Record the settled width so the resize observer can tell its own echo from a real resize
    this.lastWidth = this.getBoundingClientRect().width;
  }
}

if (!customElements.get('title-fit')) {
  customElements.define('title-fit', TitleFit);
}
